/**
 * Sub-controller for the "Share build" flyout (`view="share-build"`).
 *
 * Ports save-and-share lead submission for the flyout. Email and phone use native constraints in
 * `shareBuild.view.isml` (`type`, `required`, `pattern`, `data-missing-error`, `data-pattern-mismatch`);
 * `paintInvalidFieldMessagesInForm` maps `:invalid` controls to their `#…-error` blocks.
 *
 * Unique build ID + copy: `navigation/uniqueBuildCode.js` (shared with save-build).

 * Flyout interactions use **capture-phase** listeners on `document` so they run before delegated bubble
 * handlers in `configurator.js`, then `stopImmediatePropagation()` to avoid double submits.
 */
import analyticsHelper from 'app_honda_cars/utils/analyticsHelper';
import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

export const shareBuild = {
  /**
   * Bootstrap the share-build view. Idempotent; safe to call once at boot.
   */
  shareBuildView() {
    this.helpers.bootstrap();
  },

  helpers: {
    documentInterceptorsWired: false,

    /**
     * @returns {HTMLElement | null}
     */
    getViewRoot() {
      return document.querySelector('.configurator-modal .modal-view[view="share-build"]');
    },

    bootstrap() {
      this.wireDocumentInterceptorsOnce();

      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view !== 'share-build') {
          return;
        }

        const view = this.getViewRoot();

        if (view) {
          this.populateLocationFromFooter(view);
          this.applyInitialToggleRequiredState(view);
        }
      });
    },

    wireDocumentInterceptorsOnce() {
      if (this.documentInterceptorsWired) {
        return;
      }

      this.documentInterceptorsWired = true;
      document.addEventListener('click', e => this.onDocumentClickCapture(e), true);
      document.addEventListener('submit', e => this.onDocumentSubmitCapture(e), true);
      document.addEventListener('change', e => this.onDocumentChangeCapture(e), true);
      document.addEventListener('change', e => this.onDocumentChangeBubbleShareBuildFields(e), false);
      document.addEventListener('input', e => this.onDocumentInputBubble(e), false);
    },

    /**
     * @param {MouseEvent} event
     */
    onDocumentClickCapture(event) {
      const btn = event.target.closest?.('.save-and-share-modal-cta, .configurator-save-and-share-offline-cta');

      if (!btn || !btn.closest('.modal-view[view="share-build"]')) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const view = btn.closest('.modal-view[view="share-build"]');

      if (btn.classList.contains('save-and-share-modal-cta')) {
        this.submitCustomerShare(view);
      } else if (btn.classList.contains('configurator-save-and-share-offline-cta')) {
        this.submitDealerShare(view);
      }
    },

    /**
     * Enter-in-field submit for the flyout forms only.
     *
     * @param {SubmitEvent} event
     */
    onDocumentSubmitCapture(event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (!form.closest('.modal-view[view="share-build"]')) {
        return;
      }

      if (form.id !== 'configurator-save-and-share' && form.id !== 'offline-configurator-save-and-share') {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const view = form.closest('.modal-view[view="share-build"]');

      if (form.id === 'configurator-save-and-share') {
        this.submitCustomerShare(view);
      } else {
        this.submitDealerShare(view);
      }
    },

    /**
     * @param {Event} event
     */
    onDocumentChangeCapture(event) {
      if (event.target?.id !== 'share-details-form-toggle') {
        return;
      }

      const toggle = event.target;
      const view = toggle.closest('.modal-view[view="share-build"]');

      if (!view) {
        return;
      }

      event.stopImmediatePropagation();
      this.syncToggleDetailsBlock(view, toggle.checked);
    },

    /**
     * Clears inline invalid state while typing; messages come from HTML constraints + `paintInvalidFieldMessagesInForm`.
     *
     * @param {Event} event
     */
    onDocumentInputBubble(event) {
      const t = event.target;
      const view = t.closest?.('.modal-view[view="share-build"]');

      if (!view || !(t instanceof HTMLInputElement) || !t.id) {
        return;
      }

      const form = t.closest('form.configurator-save-and-share');

      if (!form || !view.contains(form)) {
        return;
      }

      const $err = $(form).find(`#${t.id}-error`);

      if (!$err.length) {
        return;
      }

      $err.hide().text('');
      $(t).removeClass('is-invalid');
    },

    /**
     * Clears title error when the select changes (no `input` event on `<select>`).
     *
     * @param {Event} event
     */
    onDocumentChangeBubbleShareBuildFields(event) {
      const t = event.target;

      if (t?.id !== 'details-form-title') {
        return;
      }

      const view = t.closest?.('.modal-view[view="share-build"]');

      if (!view) {
        return;
      }

      $(t).removeClass('is-invalid');
      $(view).find('#details-form-title-error').hide().text('');
    },

    /**
     * @param {HTMLElement} view
     * @param {boolean} checked
     */
    syncToggleDetailsBlock(view, checked) {
      const $view = $(view);
      const $saveAndShareBuildBlock = $view.find('.build-send-and-share-block');
      const $shareBuildPrivacyAccordion = $view.find('.share-build-privacy-accordion');
      const $onlyEmailSendBlock = $view.find('.only-build-email-send-block');
      const $saveAndShareForm = $view.find('#configurator-save-and-share');

      if (!$saveAndShareForm.length) {
        return;
      }

      if (checked) {
        $saveAndShareBuildBlock.removeClass('d-none').addClass('d-flex');
        $shareBuildPrivacyAccordion.removeClass('d-none').addClass('d-block');
        $onlyEmailSendBlock.removeClass('d-block').addClass('d-none');
        $saveAndShareForm.find('#details-form-firstname').prop('required', true);
        $saveAndShareForm.find('#details-form-lastname').prop('required', true);
        $saveAndShareForm.find('#details-form-phone').prop('required', true);
      } else {
        $saveAndShareBuildBlock.removeClass('d-flex').addClass('d-none');
        $shareBuildPrivacyAccordion.removeClass('d-block').addClass('d-none');
        $onlyEmailSendBlock.removeClass('d-none').addClass('d-block');
        $saveAndShareForm.find('#details-form-firstname').prop('required', false);
        $saveAndShareForm.find('#details-form-lastname').prop('required', false);
        $saveAndShareForm.find('#details-form-phone').prop('required', false);
      }
    },

    /**
     * @param {HTMLElement} view
     */
    populateLocationFromFooter(view) {
      const $view = $(view);

      $view.find('#configurator-save-and-share-summary').text($('#configurator-footer-location-summary').text());
      $view.find('#configurator-save-and-share-dealer-name').text($('#configurator-footer-dealer-name').text());
      $view.find('#configurator-save-and-share-dealers-address').text($('#dealers__list__store-info__address').text());
    },

    /**
     * Match `configurator.js` boot: optional fields when the contact block is off.
     *
     * @param {HTMLElement} view
     */
    applyInitialToggleRequiredState(view) {
      const $form = $(view).find('#configurator-save-and-share');

      if (!$form.length) {
        return;
      }

      if (!$form.find('#share-details-form-toggle').is(':checked')) {
        $form.find('#details-form-firstname').prop('required', false);
        $form.find('#details-form-lastname').prop('required', false);
        $form.find('#details-form-phone').prop('required', false);
      }
    },

    /**
    * @returns {string}
    */
    getDealerIdFromCookie() {
      const raw = frontendUtils.getCookie('userloc');

      if (!raw) {
        return '';
      }

      try {
        return JSON.parse(raw).dealer?.dealerId || '';
      } catch (e) {
        return '';
      }
    },

    /**
     * @returns {number | null}
     */
    getBuildYear() {
      const $productBuildYearFull = $('.internal-Vehicle-number').find('input[name="productionDate"]').attr('value');

      if (!$productBuildYearFull) {
        return null;
      }

      return new Date($productBuildYearFull).getFullYear();
    },

    /**
     * Clears Bootstrap-style invalid state before a new submit attempt.
     *
     * @param {JQuery} $form
     */
    clearInvalidFeedbackInForm($form) {
      $form.find('input.form-control, select.form-control, textarea.form-control').removeClass('is-invalid');
      $form.find('[id$="-error"]').hide().text('');
    },

    /**
     * Maps each `:invalid` control to `#${id}-error` inside the form (matches `shareBuild.view.isml`).
     * Uses `data-missing-error` / `data-pattern-mismatch` / `data-type-mismatch` when present so copy stays in HTML.
     *
     * @param {JQuery} $form
     */
    paintInvalidFieldMessagesInForm($form) {
      const formEl = $form.get(0);

      if (!formEl) {
        return;
      }

      formEl.querySelectorAll(':invalid').forEach(node => {
        if (!(node instanceof HTMLElement)) {
          return;
        }

        node.classList.add('is-invalid');

        const feedbackId = node.id ? `${node.id}-error` : '';

        if (!feedbackId) {
          return;
        }

        const $fb = $form.find(`#${feedbackId}`);

        if (!$fb.length) {
          return;
        }

        const msg = this.getConstraintMessageFromElement(node);

        $fb.text(msg || ' ').css('display', 'flex');
      });
    },

    /**
     * @param {HTMLElement} node
     * @returns {string}
     */
    getConstraintMessageFromElement(node) {
      if (!(node instanceof HTMLInputElement || node instanceof HTMLSelectElement || node instanceof HTMLTextAreaElement)) {
        return '';
      }

      const { dataset, validity } = node;

      if (validity.valueMissing && dataset.missingError) {
        return dataset.missingError;
      }

      if (validity.patternMismatch && dataset.patternMismatch) {
        return dataset.patternMismatch;
      }

      if (validity.typeMismatch && dataset.typeMismatch) {
        return dataset.typeMismatch;
      }

      return node.validationMessage || '';
    },

    /**
     * Single toast in `configuratorPageFooter.isml`: header always; body only when `shareBuildAndContact` is on.
     *
     * @param {boolean} shareBuildAndContact - `#share-details-form-toggle` checked (customer flow); offline form has no toggle → falsy.
     */
    showShareBuildSuccessToast(shareBuildAndContact) {
      const $toast = $('.toast-share-build-success');
      const $bodyToast = $toast.find('.toast-share-build-success-body');

      if (shareBuildAndContact) {
        $bodyToast.removeClass('d-none');
      } else {
        $bodyToast.addClass('d-none');
      }

      if ($toast.length && typeof $toast.toast === 'function') {
        $toast.toast('show');
      }
    },

    /**
     * @param {HTMLElement | null} view
     */
    submitCustomerShare(view) {
      if (!view) {
        return;
      }

      const $form = $(view).find('#configurator-save-and-share');
      const formEl = $form.get(0);

      if (!formEl) {
        return;
      }

      const url = $form.data('action');
      const dealerId = this.getDealerIdFromCookie();
      const selectedVariationId = $('#current-product').data('selectedvariationid');
      const shareBuildAndContact = $form.find('#share-details-form-toggle').is(':checked');
      const $modalContent = $form.closest('.modal-content');

      this.clearInvalidFeedbackInForm($form);
      $modalContent.spinner().start();

      if (!formEl.checkValidity()) {
        $modalContent.spinner().stop();
        this.paintInvalidFieldMessagesInForm($form);

        return;
      }

      const buildYear = this.getBuildYear();
      const urlParams = window.location.search;
      const urlSubstring = urlParams.substring(1);
      const metaEventId = frontendUtils.generateUUID();

      const payload = {
        pid: selectedVariationId,
        title: $form.find('#details-form-title').val(),
        firstName: $form.find('#details-form-firstname').val(),
        lastName: $form.find('#details-form-lastname').val(),
        email: $form.find('#details-form-email').val(),
        mobile: $form.find('#details-form-phone').val(),
        shareBuildAndContact,
        consentForMarketingCommunication: $form.find('#marketingConsent').is(':checked'),
        dealerId,
        urlParams: urlSubstring,
        buildYear,
        metaEventId,
        GACLIENTID: analyticsHelper.getGAClientId(),
        GATRACKID: analyticsHelper.getGATrackId(),
        GAUSERID: analyticsHelper.getGAUserId(),
        GUID: analyticsHelper.getGAGUID()
      };

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        data: payload,
        success: data => {
          if (data.messageemail) {
            $form.find('.invalid-email-error').text(data.messageemail);
          } else {
            $form.find('.invalid-email-error').empty();
            this.showShareBuildSuccessToast(shareBuildAndContact);
            $(view).closest('.configurator-modal').modal('hide');

            const shareBuildChecked = $form.find('#share-details-form-toggle').is(':checked');

            formEl.reset();
            $form.find('#share-details-form-toggle').prop('checked', shareBuildChecked);
            $form.find('.form-control.is-invalid').removeClass('is-invalid');

            const optIn = $form.find('#marketingConsent').is(':checked');
            const inputLocation = document.getElementById('configurator-footer-location-summary')?.textContent.trim() || '';
            const dealerName = document.getElementById('configurator-footer-dealer-name')?.textContent.trim() || '';
            const locationParts = inputLocation.match(/^(.*)\s(\d{4,5})$/);
            const formattedAddress = locationParts ? locationParts[1] : inputLocation;
            const postalCode = locationParts ? locationParts[2] : '';
            const summaryDataJSON = $('.js-save-summary').attr('data-car-summary-data');

            let summaryData = null;

            try {
              summaryData = summaryDataJSON ? JSON.parse(summaryDataJSON) : null;
            } catch (parseErr) {
              summaryData = null;
            }

            if (summaryData?.data?.product) {
              const productData = summaryData.data.product;
              const selectedModel = {
                segments: productData.customProps.Body || '',
                displayTitle: productData.productName || '',
                title: productData.productName || '',
                code: productData.customProps.HesCode || '',
                sf_model: productData.customProps.Model || '',
                sf_grade: productData.customProps.grade || '',
                sf_door: productData.customProps.Doors || '',
                sf_year: productData.customProps.Year || '',
                year: productData.customProps.Year || '',
                variants: []
              };
              const variantData = {
                title: productData.variationAttributes[1].displayValue || '',
                code: productData.customProps.HesCode || '',
                isDefaultVariant: false,
                transmission: productData.customProps.trans || '',
                sfGrade: productData.customProps.grade || '',
                sfDoor: productData.customProps.doors || '',
                subvariants: []
              };
              const subVariantData = {
                title: productData.variationAttributes[0].displayValue || '',
                exterior_color_code: productData.customProps.color || '',
                colorPrice: '0',
                polkParams: '_32900_0_0_0_66_1498_1248_6.2_1_1_0_0_4_142',
                isDefaultSubvariant: true,
                accesories: window.globalAccessoryData ? window.globalAccessoryData : ''
              };

              variantData.subvariants.push(subVariantData);
              selectedModel.variants.push(variantData);

              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                event: 'custom.form',
                group: 'Save build',
                category: 'configurator',
                action: 'Complete',
                data: {
                  'configurator ID': window.globalSaveuniqueCode ? window.globalSaveuniqueCode : '',
                  location: formattedAddress,
                  postcode: postalCode,
                  dealer: dealerName,
                  optIn,
                  sharedetailsopIn: shareBuildChecked
                },
                models: [selectedModel]
              });
            }
          }
        },
        complete: () => {
          $modalContent.spinner().stop();
        }
      });
    },

    /**
     * @param {HTMLElement | null} view
     */
    submitDealerShare(view) {
      if (!view) {
        return;
      }

      const $form = $(view).find('#offline-configurator-save-and-share');
      const formEl = $form.get(0);

      if (!formEl) {
        return;
      }

      const url = $form.data('action');
      const dealerId = this.getDealerIdFromCookie();
      const selectedVariationId = $('#current-product').data('selectedvariationid');

      const urlParams = window.location.search;
      const urlSubstring = urlParams.substring(1);
      const $modalContent = $form.closest('.modal-content');

      this.clearInvalidFeedbackInForm($form);
      $modalContent.spinner().start();

      if (!formEl.checkValidity()) {
        $modalContent.spinner().stop();
        this.paintInvalidFieldMessagesInForm($form);

        return;
      }

      const metaEventId = frontendUtils.generateUUID();

      const FormDataPayload = {
        pid: selectedVariationId,
        title: $form.find('#details-form-title').val(),
        firstName: $form.find('#details-form-firstname').val(),
        lastName: $form.find('#details-form-lastname').val(),
        email: $form.find('#details-form-email').val(),
        mobile: $form.find('#details-form-phone').val(),
        shareBuildAndContact: $form.find('#share-details-form-toggle').is(':checked'),
        consentForMarketingCommunication: $form.find('#marketingConsent').is(':checked'),
        dealerId,
        urlParams: urlSubstring,
        metaEventId,
        GACLIENTID: analyticsHelper.getGAClientId(),
        GATRACKID: analyticsHelper.getGATrackId(),
        GAUSERID: analyticsHelper.getGAUserId(),
        GUID: analyticsHelper.getGAGUID()
      };

      $.ajax({
        url,
        type: 'post',
        dataType: 'json',
        data: FormDataPayload,
        success: data => {
          if (data.sucess) {
            $modalContent.spinner().stop();
            $form.find('.invalid-email-error').empty();
            this.showShareBuildSuccessToast(FormDataPayload.shareBuildAndContact);
            $(view).closest('.configurator-modal').modal('hide');
          } else {
            $modalContent.spinner().stop();
            $form.find('.invalid-email-error').empty();
            $(view).closest('.configurator-modal').modal('hide');
          }
        },
        error: () => {
          $modalContent.spinner().stop();
        }
      });
    }
  }
};
